import React, { FC, useState } from "react";
import { subDays } from "date-fns";
import { useDebounce } from "react-use";
import { GridFilterModel, GridSortItem } from "@mui/x-data-grid-pro";
import {
  ADMIN_APPOINTMENTS_LIST_DURATION_DAYS,
  PAGE_SIZE_OPTIONS,
} from "../../../common/constants";
import { DateRange, DateRangePickerProps } from "../../Unknown/DateRangePicker";
import PatientInformationDrawerContainer from "../../Patient/PatientInformationDrawerContainer";
import CustomerInformationDrawerContainer from "../../Customer/CustomerInformationDrawerContainer";
import { useAppContext } from "../../Unknown/AppContext";
import Box from "../../Unknown/Box";
import AppointmentTable from "../AppointmentTable";
import usePatientDrawerContainer from "./usePatientDrawerContainer";
import useCustomerDrawerContainer from "./useCustomerDrawerContainer";
import { useTableAppointments } from "./useTableAppointments";
import useStyles from "./useStyles";

type AppointmentsTableProps = {
  role?: string;
  date: Date;
};

const defaultToDate = new Date();
const defaultFromDate = subDays(
  defaultToDate,
  ADMIN_APPOINTMENTS_LIST_DURATION_DAYS - 1,
);

const AppointmentTableContainer: FC<AppointmentsTableProps> = ({
  role,
  date,
}) => {
  const [dateRange, setDateRange] = useState<DateRange<Date>>([
    defaultFromDate,
    defaultToDate,
  ]);
  const [debouncedDateRange, setDebouncedDateRange] = useState<DateRange<Date>>(
    [defaultFromDate, defaultToDate],
  );

  useDebounce(
    () => {
      setDebouncedDateRange(dateRange);
    },
    1000,
    [dateRange],
  );

  const [filterModel, setFilterModel] = useState<GridFilterModel>({
    items: [],
  });
  const [sortModel, setSortModel] = useState<GridSortItem[]>([]);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: PAGE_SIZE_OPTIONS[0],
  });

  const { tableRoot } = useStyles();

  const {
    databaseUserData: { centerIds },
  } = useAppContext();

  const {
    appointments,
    rowCount,
    isLoading,
    isFetching,
    refetch,
    fetchAllForExport,
  } = useTableAppointments({
    centerIds,
    date,
    dateRange: role === "super_admin" ? debouncedDateRange : undefined,
    filterModel,
    sortModel,
    paginationModel,
  });

  const handleFilterModelChange = (model: GridFilterModel) => {
    setFilterModel(model);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const handleSortModelChange = (model: GridSortItem[]) => {
    setSortModel(model);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const customerDrawerContainerProps = useCustomerDrawerContainer();

  const { isOpen: isPatientDrawerOpen, props: patientDrawerContainerProps } =
    usePatientDrawerContainer({
      refetchAppointment: () => {
        refetch();
      },
    });

  const dateRangePickerProps: DateRangePickerProps<Date> = {
    value: dateRange,
    onChange: (newRange) => {
      setDateRange(newRange);
      setPaginationModel((prev) => ({ ...prev, page: 0 }));
    },
  };

  return (
    <>
      <Box className={tableRoot}>
        <AppointmentTable
          role={role}
          refetch={refetch}
          appointments={appointments}
          isLoading={isLoading}
          isFetching={isFetching && !!appointments?.length}
          dateRangePickerProps={dateRangePickerProps}
          filterModel={filterModel}
          sortModel={sortModel}
          paginationModel={paginationModel}
          onFilterModelChange={handleFilterModelChange}
          onSortModelChange={handleSortModelChange}
          onPaginationModelChange={setPaginationModel}
          rowCount={rowCount}
          fetchAllForExport={fetchAllForExport}
        />
      </Box>
      {isPatientDrawerOpen && (
        <PatientInformationDrawerContainer {...patientDrawerContainerProps} />
      )}
      <CustomerInformationDrawerContainer {...customerDrawerContainerProps} />
    </>
  );
};

export default AppointmentTableContainer;
